import {
  ContentChild,
  Self,
  ChangeDetectorRef,
  AfterContentInit,
  OnDestroy,
  Directive,
  Host,
} from '@angular/core';
import { PanelHeaderComponent } from './panel-header/panel-header.component';
import { PanelComponent } from './panel.component';

/**
 * @description
 * This directive can only be used with app-panel. This directive adds collapsible functionality to app-panel.
 * @important
 * Requires app-panel-header to be used as well
 * @usageNotes
 * ```
 * <app-panel isCollapsed:boolean appCollapsiblePanel>
 *   <app-panel-header>
 *     My Panel Header is left aligned
 *     <span>This section will automatically right align</span>
 *   </app-panel-header>
 *   My panel content
 * </app-panel>
 * ```
 */
@Directive({
    selector: '[appCollapsiblePanel]',
    standalone: false
})
export class CollapsiblePanelDirective implements AfterContentInit, OnDestroy {
  // Grab PanelHeaderComponent instance from within itself.
  @Self()
  @ContentChild(PanelHeaderComponent)
  private panelHeader?: PanelHeaderComponent;

  // Grab Panel Component instance from the directives host.
  constructor(
    @Host() @Self() private panel: PanelComponent,
    private changeDetector: ChangeDetectorRef,
  ) {}

  ngAfterContentInit() {
    if (this.panelHeader) {
      // Apply the initial value from the panel.isCollapsed binding.
      this.panelHeader.isCollapsed.next(this.panel.isCollapsed);

      this.panelHeader.isCollapsed.subscribe((isCollapsed) => {
        this.panel.isCollapsed = isCollapsed;

        // Panel component uses onPush change detection.
        this.changeDetector.markForCheck();
      });
    } else {
      throw new Error(
        'Panel Header is required when using collapsiblePanel Directive',
      );
    }
  }

  ngOnDestroy() {
    this.panelHeader?.isCollapsed.complete();
  }
}
